import React, { useState, useEffect, useRef } from 'react';
import { Game } from '../types';
import { Link } from 'react-router-dom';

interface GameSectionProps {
  title: string;
  games: Game[];
}

export default function GameSection({ title, games }: GameSectionProps) {
  const [hoveredGame, setHoveredGame] = useState<string | null>(null);
  const [currentImageIndexes, setCurrentImageIndexes] = useState<{ [key: string]: number }>({});
  const intervalRef = useRef<number>();

  useEffect(() => {
    if (hoveredGame) {
      const game = games.find(g => g.id === hoveredGame);
      if (game && game.thumbnails.length > 1) {
        // 立即切換到第二張
        setCurrentImageIndexes(prev => ({
          ...prev,
          [hoveredGame]: 1
        }));

        // 設定定時器從第三張開始輪播
        intervalRef.current = window.setInterval(() => {
          setCurrentImageIndexes(prev => ({
            ...prev,
            [hoveredGame]: prev[hoveredGame] === game.thumbnails.length - 1 ? 0 : prev[hoveredGame] + 1
          }));
        }, 1500);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hoveredGame, games]);

  return (
    <section className="my-8">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
        <span className="w-1 h-6 bg-purple-500 mr-3"></span>
        {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {games.map((game) => (
          <Link
            key={game.id}
            to={`/game/${game.id}`}
            className="group relative block rounded-lg overflow-hidden bg-[#13111d] hover:scale-105 transition-transform duration-200"
            onMouseEnter={() => setHoveredGame(game.id)}
            onMouseLeave={() => {
              setHoveredGame(null);
              setCurrentImageIndexes(prev => ({ ...prev, [game.id]: 0 }));
            }}
          >
            <div className="relative w-full aspect-video overflow-hidden">
              {game.thumbnails.map((thumbnail, index) => (
                <img
                  key={thumbnail}
                  src={thumbnail}
                  alt={`${game.title} ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transform transition-all duration-500
                    ${(currentImageIndexes[game.id] || 0) === index 
                      ? 'opacity-100 scale-100' 
                      : 'opacity-0 scale-110'}`}
                />
              ))}
            </div>
            <div className="p-4">
              <h3 className="text-lg font-semibold text-white mb-1">{game.title}</h3>
              <p className="text-gray-400 text-sm line-clamp-2">{game.description}</p>
            </div>
            <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
              {game.category}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}